// src/components/ReviewForm.js
import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  Star, Send, AlertCircle, CheckCircle, Lock
} from 'lucide-react';

const ReviewForm = ({ productId, productTitle }) => {
  const { user, userData } = useAuth();
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState(userData?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!name || !email || !comment) {
      setMessage({ type: 'error', text: 'Naam, email aur comment zaroori hai.' });
      return;
    }

    if (comment.trim().length < 10) {
      setMessage({ type: 'error', text: 'Comment kam az kam 10 characters ka hona chahiye.' });
      return;
    }

    setLoading(true);

    try {
      await addDoc(collection(db, 'reviews'), {
        productId,
        productTitle: productTitle || '',
        customerName: name,
        customerEmail: email,
        userId: user?.uid || 'guest',
        rating: Number(rating),
        comment: comment.trim(),
        approved: false, // Needs admin approval
        createdAt: new Date(),
      });

      setSubmitted(true);
      setComment('');
      setRating(5);
      setMessage({
        type: 'success',
        text: 'Shukriya! Aapka review submit ho gaya hai. Admin approval ke baad website par show hoga.'
      });
    } catch (error) {
      console.error('Review submit error:', error);
      setMessage({ type: 'error', text: 'Review submit nahi ho saka. Try again.' });
    } finally {
      setLoading(false);
    }
  };

  // If already submitted
  if (submitted) {
    return (
      <div className="rounded-2xl p-6 text-center"
        style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center anim-popIn"
            style={{ backgroundColor: 'rgba(22, 163, 74, 0.12)' }}>
            <CheckCircle size={32} style={{ color: '#16a34a' }} />
          </div>
        </div>
        <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
          Review Submitted!
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--color-secondary-text)' }}>
          Aapka review admin approval ke baad website par show hoga. Shukriya!
        </p>
        <button
          onClick={() => { setSubmitted(false); setMessage({ type: '', text: '' }); }}
          className="text-sm font-semibold underline"
          style={{ color: 'var(--color-link)' }}
        >
          Submit another review
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-6"
      style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

      <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>
        Write a Review
      </h3>
      <p className="text-xs mb-5" style={{ color: 'var(--color-secondary-text)' }}>
        Share your experience with this product
      </p>

      {/* Login Hint */}
      {!user && (
        <div className="mb-4 p-3 rounded-lg flex items-start gap-2 bg-blue-50 border border-blue-200">
          <Lock size={14} className="text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-800">
            Tip: <a href="/login" className="font-semibold underline">Login karein</a> taake aapki details auto-fill ho jayein.
          </p>
        </div>
      )}

      {message.text && (
        <div className={`text-sm p-3 rounded-lg mb-4 flex items-start gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Star Rating */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            Your Rating *
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="transition-transform hover:scale-125"
              >
                <Star
                  size={32}
                  className={
                    star <= (hoverRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }
                />
              </button>
            ))}
            <span className="ml-2 text-sm font-bold" style={{ color: 'var(--color-primary-text)' }}>
              {rating}/5
            </span>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            Your Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none"
            style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
            placeholder="e.g., Ahmed Khan"
            required
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            Your Email *
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none"
            style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
            placeholder="ahmed@example.com"
            required
          />
          <p className="text-xs mt-1" style={{ color: 'var(--color-secondary-text)' }}>
            Aapka email public nahi hoga.
          </p>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            Your Review *
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="w-full rounded-lg py-3 px-4 text-sm focus:outline-none resize-none"
            style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}
            placeholder="Product ke baare mein apni raay likhein... (minimum 10 characters)"
            required
          />
          <p className="text-xs mt-1" style={{ color: 'var(--color-secondary-text)' }}>
            {comment.length} characters
          </p>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full rounded-full py-3.5 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg ripple-btn ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
        >
          {loading ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
              Submitting...
            </>
          ) : (
            <>
              <Send size={18} /> Submit Review
            </>
          )}
        </button>

        <p className="text-xs text-center" style={{ color: 'var(--color-secondary-text)' }}>
          ℹ️ Review admin approval ke baad show hoga.
        </p>

      </form>
    </div>
  );
};

export default ReviewForm;